import React, { Fragment, useState } from "react";
import { NavLink } from "react-router-dom";
import { Box, ButtonBase, Icon, styled } from "@mui/material";

import useSettings from "app/hooks/useSettings";
import { Paragraph, Span } from "../Typography";
import MatxVerticalNavExpansionPanel from "./MatxVerticalNavExpansionPanel";

const ListLabel = styled(Paragraph)(({ theme, mode }) => ({
  fontSize: "12px",
  marginTop: "20px",
  marginLeft: "15px",
  marginBottom: "10px",
  textTransform: "uppercase",
  display: mode === "compact" ? "none" : "block",
  color: theme.palette.text.secondary,
}));

const ExtAndIntCommon = {
  display: "flex",
  alignItems: "center",
  overflow: "hidden",
  borderRadius: "0 25px 25px 0", // ⬅️ Rounded left & right corners
  height: 44,
  whiteSpace: "pre",
  marginBottom: "6px",
  textDecoration: "none",
  justifyContent: "flex-start",
  paddingLeft: "12px",
  transition: "all 200ms ease-in-out",

  // 🔹 Hover styles
  "&:hover": {
    background: "rgba(255, 255, 255, 0.05)",
    "& .icon": {
      color: "#ffeb3b", // yellow icons
    },
    "& span": {
      color: "#ffeb3b", // yellow text
    },
    "& .nav-bullet": {
      background: "#ffeb3b", // yellow bullet
    },
  },

  // 🔹 Active styles
  "&.navItemActive": {
    background: "rgba(255, 255, 255, 0.16)",
    boxShadow: "inset 4px 0 0 0 #ffffff",
    "& .icon": {
      color: "#ff9800", // orange icons
    },
    "& span": {
      color: "#c2a223ff", // orange text
    },
    "& .nav-bullet": {
      background: "#ff9800", // orange bullet
    },
  },

  "&.compactNavItem": {
    justifyContent: "center !important",
  },

  // 🔹 Default colors
  "& .icon": {
    fontSize: "20px",
    marginRight: "10px",
    color: "#ffffff",
  },
  "& span": {
    color: "#ffffff",
  },
  "& .nav-bullet": {
    background: "#ffffff", // default white bullet
  },
};

const ExternalLink = styled("a")(() => ({
  ...ExtAndIntCommon,
}));

const InternalLink = styled(Box)(() => ({
  "& a": {
    ...ExtAndIntCommon,
  },
  "& .navItemActive": {
    backgroundColor: "rgba(255, 255, 255, 0.16)",
  },
}));

const StyledText = styled(Span)(({ mode }) => ({
  fontSize: "0.875rem",
  paddingLeft: "0.8rem",
  color: "#fff",
  display: mode === "compact" ? "none" : "block",
}));
const BulletIcon = styled("div")(() => ({
  width: "8px",
  height: "8px",
  borderRadius: "50%",
  marginLeft: "24px",
  marginRight: "8px",
  background: "#ffffff", // default white, overridden on hover/active
}));

// const BulletIcon = styled("div")(({ theme }) => ({
//   width: "8px",
//   height: "8px",
//   borderRadius: "50%",
//   marginLeft: "24px",
//   marginRight: "8px",
//   background: theme.palette.primary.light,
// }));

const BadgeValue = styled("div")(() => ({
  padding: "1px 8px",
  overflow: "hidden",
  borderRadius: "300px",
}));

export default function MatxVerticalNav({ items }) {
  const { settings } = useSettings();
  const { mode } = settings.layout1Settings.leftSidebar;

  // ✅ Define state here
  const [expandedParent, setExpandedParent] = useState(null);

  const renderLevels = (data) => {
    return data.map((item, index) => {
      if (item.type === "label") {
        return (
          <ListLabel key={index} mode={mode} className="sidenavHoverShow">
            {item.label}
          </ListLabel>
        );
      }

      if (item.children) {
        return (
          <MatxVerticalNavExpansionPanel
            key={index}
            item={item}
            mode={mode}
            expandedParent={expandedParent}
            setExpandedParent={setExpandedParent} // ✅ pass function
          >
            {renderLevels(item.children)}
          </MatxVerticalNavExpansionPanel>
        );
      } else if (item.type === "extLink") {
        return (
          <ExternalLink
            key={index}
            href={item.path}
            className={mode === "compact" ? "compactNavItem" : ""}
            rel="noopener noreferrer"
            target="_blank"
          >
            <ButtonBase sx={{ width: "100%" }}>
              {item.icon ? (
                <Icon className="icon">{item.icon}</Icon>
              ) : (
                <span className="item-icon icon-text">{item.iconText}</span>
              )}
              <StyledText mode={mode} className="sidenavHoverShow">
                {item.name}
              </StyledText>
              <Box mx="auto"></Box>
              {item.badge && <BadgeValue>{item.badge.value}</BadgeValue>}
            </ButtonBase>
          </ExternalLink>
        );
      } else {
        return (
          <InternalLink key={index}>
            <NavLink
              to={item.path}
              className={({ isActive }) =>
                isActive
                  ? `navItemActive ${
                      mode === "compact" ? "compactNavItem" : ""
                    }`
                  : mode === "compact"
                  ? "compactNavItem"
                  : ""
              }
            >
              <ButtonBase sx={{ width: "100%" }}>
                {item?.icon ? (
                  <Icon className="icon" sx={{ width: 36 }}>
                    {item.icon}
                  </Icon>
                ) : (
                  <Fragment>
                    <BulletIcon
                      className="nav-bullet"
                      sx={{ display: mode === "compact" ? "none" : "block" }}
                    />
                    <Box
                      className="nav-bullet-text"
                      sx={{
                        ml: "20px",
                        fontSize: "11px",
                        display: mode !== "compact" ? "none" : "block",
                      }}
                    >
                      {item.iconText}
                    </Box>
                  </Fragment>
                )}
                <StyledText mode={mode} className="sidenavHoverShow">
                  {item.name}
                </StyledText>
                <Box mx="auto" />
                {item.badge && (
                  <BadgeValue className="sidenavHoverShow">
                    {item.badge.value}
                  </BadgeValue>
                )}
              </ButtonBase>
            </NavLink>
          </InternalLink>
        );
      }
    });
  };

  return (
    <div
      className="navigation"
      // style={{
      //   background: "#1798a6", // solid background for the menu
      //   height: "100vh", // full height of viewport
      //   display: "flex",
      //   flexDirection: "column",
      //   paddingTop: "12px",
      //   color: "#fff",
      // }}
      style={{
        background: "#0d4f85",
        height: "100%",
        minHeight: "calc(100vh - 64px)",
        paddingTop: "12px",
        color: "#fff",
      }}
    >
      {renderLevels(items)}
    </div>
  );
}
